import React from "react";
import ReactDOM from "react-dom/client";
import * as Sentry from "@sentry/react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import "@/index.css";
import App from "@/App";

// Frontend error monitoring — no-op unless REACT_APP_SENTRY_DSN is configured.
const sentryDsn = process.env.REACT_APP_SENTRY_DSN;
if (sentryDsn) {
  Sentry.init({
    dsn: sentryDsn,
    environment: process.env.REACT_APP_SENTRY_ENV || "preview",
    tracesSampleRate: Number(process.env.REACT_APP_SENTRY_TRACES_SAMPLE_RATE || 0),
  });
}

// Resilience: a transient backend hiccup (e.g. a 502 during a container
// cold-start) makes in-flight axios calls reject. If a caller hasn't attached a
// .catch(), the dev server surfaces a full-screen "Uncaught runtime errors"
// overlay — disruptive during a live demo. We swallow ONLY network/HTTP
// rejections here; genuine programming errors (TypeError, etc.) still surface.
if (typeof window !== "undefined") {
  window.addEventListener("unhandledrejection", (event) => {
    const r = event.reason;
    const isNetworkError =
      r &&
      (r.isAxiosError === true ||
        r.config ||
        r.request ||
        r.response ||
        (typeof r.message === "string" &&
          /(network error|timeout|request failed with status|status code (4|5)\d\d)/i.test(r.message)));
    if (isNetworkError) {
      event.preventDefault();
      console.warn("[sema] suppressed transient network rejection:", r?.message || r);
    }
  });
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    },
  },
});

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </React.StrictMode>,
);
